import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_POST_BY_SLUG, GET_RECENT_POSTS } from '../../graphql/queries';
import Sidebar from './sidebar';

const BlogDetails = () => {
    const { slug } = useParams();
    const publicUrl = process.env.PUBLIC_URL + '/';

    // Fetch single post by slug
    const { data, loading, error } = useQuery(GET_POST_BY_SLUG, {
        variables: { slug }
    });

    // Fetch related posts
    const { data: relatedData } = useQuery(GET_RECENT_POSTS, {
        variables: { first: 3 }
    });

    const post = data?.post;
    const relatedPosts = relatedData?.posts?.nodes || [];

    // Helper function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Helper function to get featured image
    const getFeaturedImage = () => {
        return post?.featuredImage?.node?.sourceUrl || publicUrl + 'assets/img/blog/4.png';
    };

    // Helper function to get author name
    const getAuthorName = () => {
        return post?.author?.node?.name || 'Admin';
    };

    // Helper function to get first category
    const getFirstCategory = () => {
        return post?.categories?.nodes?.[0] || { name: 'Uncategorized', slug: 'uncategorized' };
    };

    if (loading) {
        return (
            <div className="blog-page-area pd-bottom-80 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 pd-top-50 text-center">
                            <p>Loading post...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="blog-page-area pd-bottom-80 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 pd-top-50 text-center">
                            <h3>Post not found</h3>
                            <p>The post you're looking for doesn't exist or has been removed.</p>
                            <Link to="/" className="btn btn-blue mt-3">Go to Homepage</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const category = getFirstCategory();

    return (
        <div className="blog-page-area pd-bottom-80 go-top">
            <div className="container">
                <div className="row">
                    <div className="col-lg-9 pd-top-50">
                        <div className="blog-details-page-inner">
                            <div className="single-blog-inner m-0">
                                {/* Post Header */}
                                <div className="single-post-wrap style-overlay">
                                    <div className="thumb">
                                        <img
                                            src={getFeaturedImage()}
                                            alt={post?.featuredImage?.node?.altText || post.title}
                                        />
                                    </div>
                                    <div className="details pb-4">
                                        <div className="post-meta-single mb-2">
                                            <ul>
                                                <li>
                                                    <Link className="tag-base tag-blue" to={`/category/${category.slug}`}>
                                                        {category.name}
                                                    </Link>
                                                </li>
                                                <li><p><i className="fa fa-clock-o" />{formatDate(post.date)}</p></li>
                                                <li><i className="fa fa-user" />{getAuthorName()}</li>
                                            </ul>
                                        </div>
                                        <h5 className="title mt-0" dangerouslySetInnerHTML={{ __html: post.title }} />
                                    </div>
                                </div>

                                {/* Post Content */}
                                <div
                                    className="single-blog-details"
                                    dangerouslySetInnerHTML={{ __html: post.content }}
                                />

                                {/* Post Meta - Tags & Share */}
                                <div className="meta">
                                    <div className="row">
                                        <div className="col-lg-5">
                                            <div className="tags">
                                                <span>Categories:</span>
                                                {post.categories?.nodes?.map((cat, index) => (
                                                    <Link key={cat.id} to={`/category/${cat.slug}`}>
                                                        {cat.name}{index < post.categories.nodes.length - 1 ? ',' : ''}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-lg-7 text-md-right">
                                            <div className="blog-share">
                                                <span>Share:</span>
                                                <ul className="social-area social-area-2 d-inline">
                                                    <li>
                                                        <a
                                                            className="facebook-icon"
                                                            href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <i className="fa fa-facebook" />
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="twitter-icon"
                                                            href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${post.title}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <i className="fa fa-twitter" />
                                                        </a>
                                                    </li>
                                                    <li><a className="youtube-icon" href="#"><i className="fa fa-youtube-play" /></a></li>
                                                    <li><a className="instagram-icon" href="#"><i className="fa fa-instagram" /></a></li>
                                                    <li><a className="google-icon" href="#"><i className="fa fa-google-plus" /></a></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Author Area */}
                                {post.author?.node && (
                                    <div className="author-area">
                                        <div className="media">
                                            {post.author.node.avatar?.url && (
                                                <img src={post.author.node.avatar.url} alt={post.author.node.name} />
                                            )}
                                            <div className="media-body align-self-center">
                                                <h4>{post.author.node.name}</h4>
                                                <p>{post.author.node.description || 'Content creator and writer'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Related Posts */}
                            {relatedPosts.length > 0 && (
                                <div className="related-post">
                                    <div className="section-title mb-0">
                                        <h5 className="mb-0">Related Post</h5>
                                    </div>
                                    <div className="row">
                                        {relatedPosts.map((relatedPost) => (
                                            <div className="col-lg-4 col-md-6" key={relatedPost.id}>
                                                <div className="single-post-wrap">
                                                    <div className="thumb">
                                                        <img
                                                            src={relatedPost.featuredImage?.node?.sourceUrl || publicUrl + 'assets/img/post/1.png'}
                                                            alt={relatedPost.title}
                                                        />
                                                    </div>
                                                    <div className="details">
                                                        <div className="post-meta-single mb-2">
                                                            <ul>
                                                                <li>
                                                                    <Link
                                                                        className="tag-base tag-purple"
                                                                        to={`/category/${relatedPost.categories?.nodes?.[0]?.slug || 'uncategorized'}`}
                                                                        style={{borderRadius: '20px', padding: '3px 12px', fontSize: '11px'}}
                                                                    >
                                                                        {relatedPost.categories?.nodes?.[0]?.name || 'Uncategorized'}
                                                                    </Link>
                                                                </li>
                                                                <li><i className="fa fa-clock-o" />{formatDate(relatedPost.date)}</li>
                                                            </ul>
                                                        </div>
                                                        <h6 className="title">
                                                            <Link
                                                                to={`/blog/${relatedPost.slug}`}
                                                                dangerouslySetInnerHTML={{ __html: relatedPost.title }}
                                                            />
                                                        </h6>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-lg-3 pd-top-50">
                        <Sidebar />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetails;
